import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock, Shield, X } from 'lucide-react';
import useAppStore from '../../store/appStore';
import useAdminStore from '../../store/adminStore';
import { auth } from '../../config/firebase';
import getAppPages from '../../config/pageRegistry';
import { patchKalkulationMandantRoot } from '../../utils/premiumCostBridge';
import {
  clearPinSession,
  EMPTY_PIN_LOCK_CONFIG,
  hashPin,
  isNeverLockPage,
  normalizePinLockConfig,
  type PinLockConfig,
} from '../../utils/pinLockSession';

interface PagePinLockModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'setup' | 'verify' | 'config' | 'changePin';

const DigitPad: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
}> = ({ value, onChange, disabled, autoFocus, error }) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) {
      const t = window.setTimeout(() => refs.current[0]?.focus(), 40);
      return () => window.clearTimeout(t);
    }
  }, [autoFocus]);

  const setAt = (index: number, raw: string) => {
    const d = raw.replace(/\D/g, '').slice(-1);
    const next = [...value];
    next[index] = d;
    onChange(next);
    if (d && index < 3) refs.current[index + 1]?.focus();
  };

  return (
    <div className="flex justify-center gap-3">
      {value.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => setAt(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !value[i] && i > 0) {
              refs.current[i - 1]?.focus();
            }
          }}
          className={`w-12 h-14 text-center text-xl font-mono rounded-lg bg-black border ${
            error ? 'border-red-500/70' : 'border-slate-700'
          } text-white focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none`}
        />
      ))}
    </div>
  );
};

const PagePinLockModal: React.FC<PagePinLockModalProps> = ({ isOpen, onClose }) => {
  const { clientData, settings, setClientData } = useAppStore((s) => ({
    clientData: s.clientData,
    settings: s.settings,
    setClientData: s.setClientData,
  }));
  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
  const mandantUid = impersonatedClientId || auth.currentUser?.uid || '';

  const pinLockConfig = normalizePinLockConfig((clientData as any)?.pinLockConfig);

  const [step, setStep] = useState<Step>('setup');
  const [pinA, setPinA] = useState(['', '', '', '']);
  const [pinB, setPinB] = useState(['', '', '', '']);
  const [verifyPin, setVerifyPin] = useState(['', '', '', '']);
  const [lockedIds, setLockedIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const lockablePages = useMemo(() => {
    const pages = getAppPages(clientData, settings) || [];
    return pages.filter((p) => p.isVisible !== false && !isNeverLockPage(p.key));
  }, [clientData, settings]);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setPinA(['', '', '', '']);
    setPinB(['', '', '', '']);
    setVerifyPin(['', '', '', '']);
    setLockedIds(pinLockConfig.lockedPageIds.slice());
    setStep(pinLockConfig.isPinActive && pinLockConfig.pinHash ? 'verify' : 'setup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const pinComplete = (arr: string[]) => arr.every((d) => d.length === 1);
  const pinString = (arr: string[]) => arr.join('');

  const persistConfig = async (next: PinLockConfig) => {
    if (!mandantUid) throw new Error('Keine Mandanten-UID');
    const cleaned: PinLockConfig = {
      ...next,
      lockedPageIds: next.lockedPageIds.filter((id) => !isNeverLockPage(id)),
      updatedAt: new Date().toISOString(),
    };
    setClientData((prev) => ({
      ...(prev || {}),
      pinLockConfig: cleaned,
    }));
    await patchKalkulationMandantRoot(mandantUid, {
      pinLockConfig: cleaned,
      'clientData.pinLockConfig': cleaned,
    });
    return cleaned;
  };

  const handleSetupSave = async () => {
    setError('');
    if (!pinComplete(pinA) || !pinComplete(pinB)) {
      setError('Bitte 4-stellige PIN eingeben und bestätigen.');
      return;
    }
    if (pinString(pinA) !== pinString(pinB)) {
      setError('PIN und Bestätigung stimmen nicht überein.');
      return;
    }
    if (!mandantUid) {
      setError('Keine Session — bitte neu anmelden.');
      return;
    }
    setBusy(true);
    try {
      const pinHash = await hashPin(pinString(pinA), mandantUid);
      await persistConfig({
        isPinActive: true,
        pinHash,
        lockedPageIds: [],
      });
      clearPinSession(mandantUid);
      setLockedIds([]);
      setStep('config');
      setPinA(['', '', '', '']);
      setPinB(['', '', '', '']);
    } catch (e) {
      console.error(e);
      setError('Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (!pinComplete(verifyPin) || !mandantUid) {
      setError('Bitte Master-PIN eingeben.');
      return;
    }
    setBusy(true);
    try {
      const hashed = await hashPin(pinString(verifyPin), mandantUid);
      if (hashed !== pinLockConfig.pinHash) {
        setError('PIN ungültig.');
        setVerifyPin(['', '', '', '']);
        return;
      }
      setStep('config');
      setVerifyPin(['', '', '', '']);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLocks = async () => {
    setError('');
    if (!pinLockConfig.pinHash) {
      setError('Kein PIN gesetzt.');
      return;
    }
    setBusy(true);
    try {
      await persistConfig({
        isPinActive: true,
        pinHash: pinLockConfig.pinHash,
        lockedPageIds: lockedIds.filter((id) => !isNeverLockPage(id)),
      });
      onClose();
    } catch (e) {
      console.error(e);
      setError('Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleChangePin = async () => {
    setError('');
    if (!pinComplete(pinA) || !pinComplete(pinB)) {
      setError('Bitte neue PIN eingeben und bestätigen.');
      return;
    }
    if (pinString(pinA) !== pinString(pinB)) {
      setError('PIN und Bestätigung stimmen nicht überein.');
      return;
    }
    if (!mandantUid) return;
    setBusy(true);
    try {
      const pinHash = await hashPin(pinString(pinA), mandantUid);
      await persistConfig({
        isPinActive: true,
        pinHash,
        lockedPageIds: lockedIds.filter((id) => !isNeverLockPage(id)),
      });
      clearPinSession(mandantUid);
      setStep('config');
      setPinA(['', '', '', '']);
      setPinB(['', '', '', '']);
    } catch (e) {
      console.error(e);
      setError('PIN ändern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Master-PIN deaktivieren und alle Seitensperren aufheben?')) return;
    setBusy(true);
    try {
      await persistConfig({ ...EMPTY_PIN_LOCK_CONFIG });
      if (mandantUid) clearPinSession(mandantUid);
      onClose();
    } catch (e) {
      console.error(e);
      setError('Deaktivieren fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const togglePage = (key: string) => {
    if (isNeverLockPage(key)) return;
    setLockedIds((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const title =
    step === 'setup'
      ? 'Master-PIN einrichten'
      : step === 'verify'
        ? 'Master-PIN bestätigen'
        : step === 'changePin'
          ? 'Master-PIN ändern'
          : 'Seiten-Sperre konfigurieren';

  return (
    <div className="fixed inset-0 z-[100000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
              {step === 'config' ? (
                <Shield className="w-4 h-4 text-[#E6C299]" />
              ) : (
                <Lock className="w-4 h-4 text-amber-400/90" />
              )}
            </div>
            <h2 className="text-base font-bold text-white tracking-wide font-sans">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors p-1"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 custom-scrollbar">
          {step === 'setup' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-400 font-sans leading-relaxed">
                Vergib einen 4-stelligen Master-PIN. Startseite und Jahres-Akte bleiben dauerhaft frei.
              </p>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Neue PIN
                </label>
                <DigitPad value={pinA} onChange={setPinA} autoFocus disabled={busy} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  PIN bestätigen
                </label>
                <DigitPad value={pinB} onChange={setPinB} disabled={busy} />
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-400 font-sans leading-relaxed">
                Zur Konfiguration bitte den Master-PIN eingeben.
              </p>
              <DigitPad
                value={verifyPin}
                onChange={setVerifyPin}
                autoFocus
                disabled={busy}
                error={!!error}
              />
            </div>
          )}

          {step === 'changePin' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-400 font-sans leading-relaxed">
                Neue 4-stellige PIN vergeben. Bestehende Seitensperren bleiben erhalten.
              </p>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Neue PIN
                </label>
                <DigitPad value={pinA} onChange={setPinA} autoFocus disabled={busy} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  PIN bestätigen
                </label>
                <DigitPad value={pinB} onChange={setPinB} disabled={busy} />
              </div>
            </div>
          )}

          {step === 'config' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 font-sans">
                Checkbox aktiv = Seite gesperrt. System-Standard frei: Startseite, Jahres-Akte.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setLockedIds(lockablePages.map((p) => p.key))}
                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md border border-slate-700 text-slate-300 hover:border-[#E6C299]/50 hover:text-[#E6C299] transition-colors"
                >
                  Alle sperren
                </button>
                <button
                  type="button"
                  onClick={() => setLockedIds([])}
                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md border border-slate-700 text-slate-300 hover:border-[#E6C299]/50 hover:text-[#E6C299] transition-colors"
                >
                  Keine
                </button>
              </div>
              <ul className="space-y-1 max-h-64 overflow-y-auto border border-slate-800 rounded-lg divide-y divide-slate-800/80">
                {lockablePages.map((p) => {
                  const checked = lockedIds.includes(p.key);
                  return (
                    <li key={p.key}>
                      <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-800/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePage(p.key)}
                          className="rounded border-slate-600 bg-black text-[#E6C299] focus:ring-[#E6C299]"
                        />
                        <span className="text-sm text-slate-200 font-sans flex-1">{p.label}</span>
                        {checked && <Lock className="w-3.5 h-3.5 text-amber-400/80" />}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {error && (
            <p className="mt-4 text-xs text-red-400 font-bold text-center tracking-wide">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2">
          {step === 'config' ? (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPinA(['', '', '', '']);
                    setPinB(['', '', '', '']);
                    setError('');
                    setStep('changePin');
                  }}
                  className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                >
                  PIN ändern
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeactivate()}
                  disabled={busy}
                  className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-red-400/80 hover:text-red-400 transition-colors disabled:opacity-40"
                >
                  Deaktivieren
                </button>
              </div>
              <button
                type="button"
                onClick={() => void handleSaveLocks()}
                disabled={busy}
                className="px-5 py-2.5 rounded-lg bg-[#E6C299]/15 border border-[#E6C299]/40 text-[#E6C299] text-xs font-black uppercase tracking-widest hover:bg-[#E6C299]/25 transition-colors disabled:opacity-40"
              >
                {busy ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : step === 'verify' ? (
            <button
              type="button"
              onClick={() => void handleVerify()}
              disabled={busy || !pinComplete(verifyPin)}
              className="ml-auto px-5 py-2.5 rounded-lg bg-[#E6C299]/15 border border-[#E6C299]/40 text-[#E6C299] text-xs font-black uppercase tracking-widest hover:bg-[#E6C299]/25 transition-colors disabled:opacity-40"
            >
              Weiter
            </button>
          ) : step === 'changePin' ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setError('');
                  setStep('config');
                }}
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-white"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void handleChangePin()}
                disabled={busy}
                className="px-5 py-2.5 rounded-lg bg-[#E6C299]/15 border border-[#E6C299]/40 text-[#E6C299] text-xs font-black uppercase tracking-widest hover:bg-[#E6C299]/25 disabled:opacity-40"
              >
                PIN speichern
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void handleSetupSave()}
              disabled={busy}
              className="ml-auto px-5 py-2.5 rounded-lg bg-[#E6C299]/15 border border-[#E6C299]/40 text-[#E6C299] text-xs font-black uppercase tracking-widest hover:bg-[#E6C299]/25 disabled:opacity-40"
            >
              PIN festlegen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagePinLockModal;
